import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, CheckCircle2, Clock, CreditCard, FileUp, Gift, Smartphone, Tag, X, XCircle } from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Alert, Badge, Button, Card, Checkbox, Chips, Empty, errorText, Field, Input, Loading, PageHeader, Table, Td, Th, Tr, useToast } from '../components/ui';
import { api, get, post, put } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date, dateTime, mt } from '../lib/format';
import { AffiliatePanelView, type PanelData } from './partners';
import { getBaseHost } from '../lib/tenant-host';

type Method = 'mpesa' | 'emola' | 'mkesh' | 'card' | 'transfer';
type Intent = {
  id: string; reference: string; plan_code: string; months: number; amount: string; fee_amount: string; plan_amount: string; method: Method; method_label: string; status: string;
  failure_reason: string | null; msisdn: string | null; has_proof: boolean; created_at: string; expires_at: string; paid_at: string | null;
  list_amount: string; discount_pct: string; discount_amount: string; promo_code: string | null; promo_amount: string; subtotal: string; vat_pct: string; vat_amount: string;
};
type Quote = {
  list_amount: string; discount_pct: string; discount_amount: string; promo_code: string | null; promo_amount: string; subtotal: string;
  vat_pct: string; vat_amount: string; fee_pct: string; fee_amount: string; total: string;
};
type Next =
  | { kind: 'redirect'; url: string }
  | { kind: 'form'; url: string; fields: Record<string, string> }
  | { kind: 'wait'; message: string }
  | { kind: 'transfer'; accounts: { bank: string; holder: string; account: string; nib: string }[]; instructions: string | null };

/** Carteiras com o logótipo do operador (public/pagamentos); os outros meios com ícone. */
const METHOD_LOGO: Partial<Record<Method, string>> = { mpesa: '/pagamentos/mpesa.svg', emola: '/pagamentos/emola.svg', mkesh: '/pagamentos/mkesh.svg' };
const METHOD_ICON: Record<Method, ReactNode> = {
  mpesa: <Smartphone className="size-5" />, emola: <Smartphone className="size-5" />, mkesh: <Smartphone className="size-5" />, card: <CreditCard className="size-5" />, transfer: <Banknote className="size-5" />,
};
function MethodMark({ id, label }: { id: Method; label: string }) {
  const src = METHOD_LOGO[id];
  return (
    <span className="grid h-11 w-16 shrink-0 place-items-center rounded-lg bg-white px-1.5 ring-1 ring-black/[.08]">
      {src ? <img src={src} alt={label} className="max-h-9 w-auto" /> : <span className="text-[var(--muted)]">{METHOD_ICON[id]}</span>}
    </span>
  );
}
const STATUS: Record<string, { label: string; tone: 'blue' | 'green' | 'amber' | 'red' | 'gray' }> = {
  pending: { label: 'Pendente', tone: 'blue' }, processing: { label: 'À espera de confirmação', tone: 'blue' }, awaiting_review: { label: 'Em verificação', tone: 'amber' },
  paid: { label: 'Pago', tone: 'green' }, failed: { label: 'Falhado', tone: 'red' }, expired: { label: 'Expirado', tone: 'gray' }, cancelled: { label: 'Cancelado', tone: 'gray' },
};
/** Carteiras móveis: pedem o número que recebe o pedido de PIN. */
const WALLET: Partial<Record<Method, { label: string; placeholder: string }>> = {
  mpesa: { label: 'M-Pesa (84/85)', placeholder: '84 123 4567' },
  emola: { label: 'e-Mola (86/87)', placeholder: '86 123 4567' },
  mkesh: { label: 'mKesh (82/83)', placeholder: '82 123 4567' },
};
const OPEN = ['pending', 'processing'];
const pctLabel = (pct: string) => `${Number(pct).toLocaleString('pt-PT')}%`;
const monthsLabel = (m: number) => (m === 1 ? '1 mês' : `${m} meses`);

/** Página alojada do banco que exige POST assinado: submete-se um formulário (o cartão é introduzido lá, nunca aqui). */
function submitForm(url: string, fields: Record<string, string>) {
  const f = Object.assign(document.createElement('form'), { method: 'POST', action: url });
  for (const [name, value] of Object.entries(fields)) f.appendChild(Object.assign(document.createElement('input'), { type: 'hidden', name, value }));
  document.body.appendChild(f);
  f.submit();
}

export function BillingPage() {
  const { session, can, reload } = useAuth();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['billing'], queryFn: () => get('/billing') });
  const [plan, setPlan] = useState('');
  const [months, setMonths] = useState(12);
  const [method, setMethod] = useState<Method | ''>('');
  const [msisdn, setMsisdn] = useState('');
  const [busy, setBusy] = useState(false);
  const [transferNext, setTransferNext] = useState<Extract<Next, { kind: 'transfer' }> | null>(null);
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState('');
  const pedido = params.get('pedido');

  const d = q.data;
  const plans: any[] = d?.plans ?? [];
  const methods: { id: Method; label: string; fee_pct: string }[] = d?.methods ?? [];
  const selPlan = plan || (plans.find((p) => p.code === session?.tenant.plan_code) ?? plans[0])?.code || '';
  const selMethod = method || methods[0]?.id || '';
  const p = plans.find((x) => x.code === selPlan);
  const feePct = methods.find((m) => m.id === selMethod)?.fee_pct ?? '0';
  const hasFee = Number(feePct) > 0;
  // Resumo da compra calculado pelo servidor (o mesmo cálculo do pedido): descontos, IVA e taxa.
  const quote = useQuery({
    queryKey: ['billing', 'quote', selPlan, months, selMethod, promo],
    queryFn: () => post<{ quote: Quote; promo_error: string | null }>('/billing/quote', { plan: selPlan, months, method: selMethod, promo: promo || null }),
    enabled: !!selPlan && !!selMethod && !pedido, placeholderData: keepPreviousData,
  });
  const qt = quote.data?.quote;
  const promoError = quote.data && promo ? quote.data.promo_error : null;
  const promoOk = !!promo && !!qt?.promo_code && !quote.isFetching;
  const applyPromo = () => setPromo(promoInput.trim().toUpperCase());
  const clearPromo = () => { setPromo(''); setPromoInput(''); };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!selPlan || !selMethod) return;
    setBusy(true);
    try {
      const r = await post<{ intent: Intent; next: Next }>('/billing/checkout', { plan: selPlan, months, method: selMethod, msisdn: msisdn || null, promo: promoOk ? promo : null });
      qc.invalidateQueries({ queryKey: ['billing'] });
      if (r.next.kind === 'redirect') { window.location.assign(r.next.url); return; }
      if (r.next.kind === 'form') { submitForm(r.next.url, r.next.fields); return; }
      if (r.next.kind === 'transfer') setTransferNext(r.next);
      setParams({ pedido: r.intent.id });
    } catch (err) { toast(errorText(err), 'error'); } finally { setBusy(false); }
  };

  if (q.isLoading) return <Loading />;
  if (!can('billing:view')) return <Empty title="Sem acesso">Só o dono da empresa gere a subscrição.</Empty>;

  return (
    <>
      <PageHeader eyebrow="Definições" title="Subscrição e pagamento"
        subtitle={`${session?.tenant.legal_name} · plano ${session?.tenant.plan_name}${session?.subscription.until ? ` · válido até ${date(session.subscription.until)}` : ''}`} />

      {params.get('erro') && <div className="mb-4"><Alert tone="error" title="A resposta do banco não foi aceite">Se o valor foi debitado, contacte o suporte com a referência do pedido.</Alert></div>}
      {pedido && <IntentTracker id={pedido} transfer={transferNext ?? (d?.transfer ? { kind: 'transfer', ...d.transfer } : null)}
        onClose={() => { setParams({}); setTransferNext(null); }} onPaid={() => { void reload(); qc.invalidateQueries({ queryKey: ['billing'] }); }} />}

      {!pedido && (
        !methods.length ? (
          <Alert tone="warn" title="Pagamento online ainda indisponível">Contacte o suporte do Makila para activar ou renovar o plano.</Alert>
        ) : (
          <form onSubmit={submit} className="grid gap-6 lg:grid-cols-[1fr_340px]">
            <div className="space-y-6">
              <Card title="1. Plano">
                <div className="grid gap-3 sm:grid-cols-3">
                  {plans.map((x) => (
                    <button type="button" key={x.code} onClick={() => setPlan(x.code)}
                      className={`rounded-xl border p-4 text-left transition ${x.code === selPlan ? 'border-brand-600 ring-2 ring-brand-600/30' : 'border-[var(--line)] hover:border-brand-400'}`}>
                      <div className="font-semibold">{x.name}</div>
                      <div className="mt-1 text-xl font-bold tabular-nums">{mt(x.price_monthly)} <span className="text-xs font-medium text-[var(--muted)]">MT/mês</span></div>
                      <div className="mt-1 text-xs text-[var(--muted)]">{x.max_users ?? '∞'} utilizadores · {x.max_documents_month ?? '∞'} docs/mês</div>
                    </button>
                  ))}
                </div>
              </Card>
              <Card title="2. Duração">
                <Chips value={String(months) as any} onChange={(v) => setMonths(Number(v))}
                  options={(d?.months ?? [1, 3, 6, 12, 24]).map((m: number) => ({ id: String(m) as any, label: m >= 12 ? `${monthsLabel(m)} (preço anual)` : monthsLabel(m) }))} />
              </Card>
              <Card title="3. Meio de pagamento">
                <div className="grid gap-3 sm:grid-cols-2">
                  {methods.map((m) => (
                    <button type="button" key={m.id} onClick={() => setMethod(m.id)}
                      className={`flex items-center gap-3 rounded-xl border p-3 text-left ${m.id === selMethod ? 'border-brand-600 ring-2 ring-brand-600/30' : 'border-[var(--line)] hover:border-brand-400'}`}>
                      <MethodMark id={m.id} label={m.label} /><span className="font-medium">{m.label}</span>
                      {Number(m.fee_pct) > 0 && <span className="ml-auto text-xs text-[var(--muted)]">+{pctLabel(m.fee_pct)} taxa</span>}
                    </button>
                  ))}
                </div>
                {selMethod && WALLET[selMethod] && (
                  <Field label={`Número ${WALLET[selMethod]!.label}`} required className="mt-4">
                    <Input inputMode="tel" autoComplete="tel" value={msisdn} onChange={(e) => setMsisdn(e.target.value)} placeholder={WALLET[selMethod]!.placeholder} />
                  </Field>
                )}
                {hasFee && <div className="mt-4"><Alert tone="info" title={`Taxa de transacção de ${pctLabel(feePct)}`}>
                  Os pagamentos por {methods.filter((m) => Number(m.fee_pct) > 0).map((m) => m.label).join(', ')} estão sujeitos a uma taxa de transacção de {pctLabel(feePct)} sobre o valor a pagar (depois dos descontos), acrescida ao total.{methods.some((m) => m.id === 'transfer') ? ' A transferência bancária não tem taxa.' : ''}
                </Alert></div>}
                {selMethod === 'card' && <p className="mt-3 text-xs text-[var(--muted)]">Vai ser levado à página segura do banco. Os dados do cartão são introduzidos lá — o Makila nunca os recebe.</p>}
                {selMethod === 'transfer' && <p className="mt-3 text-xs text-[var(--muted)]">Recebe as contas e uma referência; depois de transferir, envie o comprovativo. A activação é feita depois de confirmarmos o crédito (normalmente no mesmo dia útil).</p>}
              </Card>
            </div>
            <Card title="Resumo da compra" className="h-fit lg:sticky lg:top-6">
              {p && <>
                <div className="flex justify-between text-sm font-medium"><span>Plano {p.name}</span><span>{monthsLabel(months)}</span></div>
                <dl className={`mt-3 space-y-1.5 text-sm transition-opacity ${quote.isFetching ? 'opacity-60' : ''}`}>
                  <Line label="Preço do plano" value={qt?.list_amount ?? p.prices[months]} />
                  {qt && Number(qt.discount_amount) > 0 && <Line label={`Desconto do cliente (${pctLabel(qt.discount_pct)})`} value={qt.discount_amount} minus />}
                  {qt?.promo_code && Number(qt.promo_amount) > 0 && <Line label={<>Código <span className="font-mono">{qt.promo_code}</span></>} value={qt.promo_amount} minus />}
                  <div className="border-t border-dashed border-[var(--line)] pt-1.5"><Line label="Subtotal" value={qt?.subtotal ?? p.prices[months]} strong /></div>
                  <Line label={`IVA (${pctLabel(qt?.vat_pct ?? d?.vat_pct ?? '0')})`} value={qt?.vat_amount ?? '0.00'} />
                  {hasFee && <Line label={`Taxa de transacção (${pctLabel(feePct)})`} value={qt?.fee_amount ?? '0.00'} />}
                </dl>
                <div className="mt-3 flex items-baseline justify-between border-t border-[var(--line)] pt-3"><span className="text-sm text-[var(--muted)]">Total</span><span className="text-2xl font-bold tabular-nums">{mt(qt?.total ?? p.prices[months])} MT</span></div>

                <div className="mt-4">
                  {promoOk ? (
                    <div className="flex items-center gap-2 rounded-lg bg-green-600/10 px-3 py-2 text-sm text-green-800 dark:text-green-300">
                      <Tag className="size-4" /><span>Código <b className="font-mono">{promo}</b> aplicado</span>
                      <button type="button" onClick={clearPromo} className="ml-auto rounded p-0.5 hover:bg-black/5" aria-label="Retirar o código"><X className="size-4" /></button>
                    </div>
                  ) : (
                    <Field label="Código promocional" error={promoError ?? undefined}>
                      <div className="flex gap-2">
                        <Input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} placeholder="Ex.: NATAL25" maxLength={30} className="font-mono"
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyPromo(); } }} leading={<Tag className="size-4" />} />
                        <Button type="button" variant="secondary" onClick={applyPromo} disabled={!promoInput.trim()} loading={!!promo && quote.isFetching}>Aplicar</Button>
                      </div>
                    </Field>
                  )}
                </div>
                <p className="mt-3 text-xs text-[var(--muted)]">Se a subscrição ainda estiver válida, o novo período começa no dia a seguir ao fim do actual.</p>
              </>}
              <Button type="submit" className="mt-4 w-full" loading={busy} disabled={!selPlan || !selMethod || !can('billing:create')}>
                {selMethod === 'transfer' ? 'Obter dados para transferência' : selMethod === 'card' ? 'Pagar com cartão' : 'Pagar'}
              </Button>
            </Card>
          </form>
        )
      )}

      {can('billing:view') && <AffiliateCard canJoin={can('billing:create')} />}

      <Card title="Pedidos de pagamento" className="mt-8" padded={false}>
        {!d?.intents?.length ? <div className="p-5"><Empty title="Ainda sem pedidos" /></div> : (
          <Table><thead><tr><Th>Data</Th><Th>Referência</Th><Th>Plano</Th><Th>Meio</Th><Th className="text-right">Valor</Th><Th>Estado</Th></tr></thead>
            <tbody>{d.intents.map((i: Intent) => (
              <Tr key={i.id} onClick={() => setParams({ pedido: i.id })}>
                <Td className="whitespace-nowrap">{dateTime(i.created_at)}</Td><Td className="font-mono text-xs">{i.reference}</Td>
                <Td>{i.plan_code} · {monthsLabel(i.months)}</Td><Td>{i.method_label}</Td><Td className="text-right tabular-nums">{mt(i.amount)}</Td>
                <Td><Badge tone={STATUS[i.status]?.tone ?? 'gray'} dot>{STATUS[i.status]?.label ?? i.status}</Badge></Td>
              </Tr>))}</tbody></Table>
        )}
      </Card>
    </>
  );
}

/** Programa de parceiros: o cliente recomenda o Makila e ganhe comissão (adesão num clique). */
function AffiliateCard({ canJoin }: { canJoin: boolean }) {
  const qc = useQueryClient(); const toast = useToast();
  const q = useQuery({ queryKey: ['billing', 'affiliate'], queryFn: () => get<{ panel: PanelData | null }>('/billing/affiliate') });
  const [accept, setAccept] = useState(false);
  const [waOptin, setWaOptin] = useState(true);
  const [busy, setBusy] = useState(false);
  // Os termos vivem no site público (domínio principal), não no subdomínio da empresa.
  const [base, setBase] = useState('');
  useEffect(() => { getBaseHost().then(setBase).catch(() => {}); }, []);
  if (q.isLoading) return null;
  const panel = q.data?.panel;
  if (panel) return <Card title="Programa de parceiros" subtitle="Recomende o Makila e receba comissão sobre os pagamentos das empresas que trouxer." className="mt-8"><AffiliatePanelView data={panel} compact
    onSavePayout={async (body) => { await put('/billing/affiliate/payout', body); await qc.invalidateQueries({ queryKey: ['billing', 'affiliate'] }); }}
    onSaveWhatsapp={async (optin) => { await put('/billing/affiliate/whatsapp', { optin }); await qc.invalidateQueries({ queryKey: ['billing', 'affiliate'] }); }} /></Card>;
  return (
    <Card className="mt-8">
      <div className="flex flex-col gap-5 md:flex-row md:items-center">
        <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-brand-600/10 text-brand-700"><Gift className="size-6" /></span>
        <div className="flex-1">
          <h2 className="text-lg font-semibold">Recomende o Makila e ganhe 20% durante um ano</h2>
          <p className="mt-1 text-sm text-[var(--muted)]">Por cada empresa que se registar pelo seu link e pagar, recebe 20% dos pagamentos dela nos primeiros 12 meses — em dinheiro ou em crédito na sua subscrição. A empresa indicada ganha 10% no primeiro pagamento.</p>
          <div className="mt-3"><Checkbox checked={accept} onChange={setAccept} label={<>Aceito os <a href={base ? `${window.location.protocol}//${base}/parceiros/termos` : '/parceiros/termos'} target="_blank" rel="noopener" className="font-medium text-brand-700 underline">Termos do Programa de Parceiros</a></>} /></div>
          <div className="mt-2"><Checkbox checked={waOptin} onChange={setWaOptin} label="Aceito receber mensagens da equipa Makila por WhatsApp sobre o programa e o convite para a comunidade de parceiros" /></div>
        </div>
        <Button icon={<Gift className="size-4" />} loading={busy} disabled={!accept || !canJoin} onClick={async () => {
          setBusy(true);
          try { await post('/billing/affiliate/join', { whatsappOptin: waOptin }); toast('Bem-vindo ao Programa de Parceiros'); qc.invalidateQueries({ queryKey: ['billing', 'affiliate'] }); }
          catch (e) { toast(errorText(e), 'error'); } finally { setBusy(false); }
        }}>Aderir ao programa</Button>
      </div>
    </Card>
  );
}

function Line({ label, value, minus, strong }: { label: ReactNode; value: string; minus?: boolean; strong?: boolean }) {
  return (
    <div className={`flex justify-between gap-3 ${strong ? 'font-semibold' : ''}`}>
      <dt className={strong ? '' : 'text-[var(--muted)]'}>{label}</dt>
      <dd className={`tabular-nums ${minus ? 'text-green-700 dark:text-green-400' : ''}`}>{minus ? '−' : ''}{mt(value)} MT</dd>
    </div>
  );
}

/** Acompanha um pedido: espera o PIN, o regresso do banco, ou recebe o comprovativo da transferência. */
function IntentTracker({ id, transfer, onClose, onPaid }: { id: string; transfer: Extract<Next, { kind: 'transfer' }> | null; onClose: () => void; onPaid: () => void }) {
  const q = useQuery({
    queryKey: ['billing', 'intent', id], queryFn: () => get<{ intent: Intent }>(`/billing/intents/${id}`),
    refetchInterval: (query) => (OPEN.includes(query.state.data?.intent.status ?? 'pending') && query.state.data?.intent.method !== 'transfer' ? 3000 : false),
  });
  const it = q.data?.intent;
  const paidOnce = useRef(false);
  useEffect(() => { if (it?.status === 'paid' && !paidOnce.current) { paidOnce.current = true; onPaid(); } }, [it?.status, onPaid]);
  const toast = useToast();
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);

  if (q.isLoading) return <Card><Loading rows={3} /></Card>;
  if (!it) return <Alert tone="error" title="Pedido não encontrado">{errorText(q.error)}</Alert>;

  const upload = async (file: File) => {
    const fd = new FormData(); fd.append('file', file);
    setUploading(true);
    try { await api('POST', `/billing/intents/${it.id}/proof`, fd); toast('Comprovativo enviado. Vamos confirmar e avisar por email.'); qc.invalidateQueries({ queryKey: ['billing'] }); }
    catch (e) { toast(errorText(e), 'error'); } finally { setUploading(false); }
  };

  return (
    <Card className="mb-6" title={<span className="flex items-center gap-2">Pedido <span className="font-mono">{it.reference}</span></span>}
      subtitle={[`${it.method_label} · ${mt(it.amount)} MT`, `${it.plan_code} ${monthsLabel(it.months)}`,
        Number(it.discount_amount) > 0 && `desconto ${mt(it.discount_amount)} MT`, it.promo_code && `código ${it.promo_code} −${mt(it.promo_amount)} MT`,
        Number(it.fee_amount) > 0 && `inclui ${mt(it.fee_amount)} MT de taxa de transacção`].filter(Boolean).join(' · ')}
      actions={<Button size="sm" variant="secondary" onClick={onClose}>{it.status === 'paid' ? 'Fechar' : 'Novo pagamento'}</Button>}>
      {it.status === 'paid' && <Alert tone="success" title="Pagamento recebido">A subscrição foi activada. Enviámos a confirmação por email.</Alert>}
      {it.status === 'processing' && <div className="flex items-center gap-3 text-sm"><Clock className="size-5 animate-pulse text-brand-600" />Confirme o pagamento no telemóvel {it.msisdn} com o seu PIN. Esta página actualiza sozinha.</div>}
      {it.status === 'pending' && it.method !== 'transfer' && <div className="flex items-center gap-3 text-sm"><Clock className="size-5 text-brand-600" />À espera da confirmação do operador…</div>}
      {(it.status === 'failed' || it.status === 'expired' || it.status === 'cancelled') && <Alert tone="error" title={STATUS[it.status]?.label}>{it.failure_reason ?? 'O pagamento não foi concluído.'} Pode tentar de novo.</Alert>}
      {it.status === 'awaiting_review' && <Alert tone="warn" title="Em verificação">{it.failure_reason ?? 'Recebemos o comprovativo. Vamos confirmar o crédito na conta e activar a subscrição.'}</Alert>}

      {it.method === 'transfer' && ['pending', 'awaiting_review', 'expired'].includes(it.status) && (
        <div className="mt-4 space-y-4">
          <div className="rounded-xl bg-brand-600/5 p-4 text-sm">
            <p>Transfira <b className="tabular-nums">{mt(it.amount)} MT</b> para uma das contas abaixo e escreva no descritivo a referência <b className="font-mono">{it.reference}</b>. Válido até {dateTime(it.expires_at)}.</p>
            {transfer?.instructions && <p className="mt-2 text-[var(--muted)]">{transfer.instructions}</p>}
          </div>
          {transfer?.accounts.length ? (
            <Table compact><thead><tr><Th>Banco</Th><Th>Titular</Th><Th>Conta</Th><Th>NIB</Th></tr></thead>
              <tbody>{transfer.accounts.map((a) => <tr key={a.account}><Td>{a.bank}</Td><Td>{a.holder}</Td><Td className="font-mono">{a.account}</Td><Td className="font-mono">{a.nib}</Td></tr>)}</tbody></Table>
          ) : null}
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-[var(--line)] p-4 hover:border-brand-400">
            <FileUp className="size-5" />
            <span className="text-sm">{it.has_proof ? 'Comprovativo recebido — enviar outro' : 'Enviar comprovativo (PDF ou imagem, até 1 MB)'}</span>
            <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="sr-only" disabled={uploading}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ''; }} />
            {uploading && <span className="text-xs text-[var(--muted)]">A enviar…</span>}
          </label>
          {it.status === 'pending' && <Button size="sm" variant="ghost" icon={<XCircle className="size-4" />} onClick={async () => {
            try { await post(`/billing/intents/${it.id}/cancel`); onClose(); qc.invalidateQueries({ queryKey: ['billing'] }); } catch (e) { toast(errorText(e), 'error'); }
          }}>Cancelar pedido</Button>}
        </div>
      )}
      {it.status === 'paid' && <p className="mt-3 flex items-center gap-2 text-sm text-[var(--muted)]"><CheckCircle2 className="size-4 text-green-600" />Pago a {dateTime(it.paid_at!)}</p>}
    </Card>
  );
}
